import { useEffect } from 'react';
import { ControllerRenderProps, UseFormReturn } from 'react-hook-form';

import { FormField, FormItem, FormMessage } from '@@/ui/primitives';
import { Field } from '../../../../schemas/entity';
import { CreateItemPayload } from '../../../../schemas/item';
import { ConnectResourceInput } from '../../../shared/field-inputs';
import { ConnectResourceType } from '../../../../api/api-entities';
import { isLegacyConnectResourceValue, normalizeConnectResource } from '../../../../common/connect-resource';

function ConnectResourceFieldContent(props: {
  formField: ControllerRenderProps<CreateItemPayload['attributes'], string>;
  resourceType: ConnectResourceType;
  label: string;
  disabled: boolean;
}) {
  const { formField, resourceType, label, disabled } = props;

  const normalizedValue = normalizeConnectResource(formField.value, resourceType);

  useEffect(() => {
    if (isLegacyConnectResourceValue(formField.value) && normalizedValue) {
      formField.onChange(normalizedValue);
    }
  }, [formField, normalizedValue]);

  return (
    <FormItem>
      <ConnectResourceInput
        resourceType={resourceType}
        value={normalizedValue}
        onChange={formField.onChange}
        label={label}
        disabled={disabled}
      />
      <FormMessage />
    </FormItem>
  );
}

interface ConnectResourceFieldRendererProps {
  fieldKey: string;
  field: Field;
  form: UseFormReturn<CreateItemPayload['attributes']>;
  label: string;
  disabled?: boolean;
}

export function ConnectResourceFieldRenderer({
  fieldKey,
  field,
  form,
  label,
  disabled = false
}: ConnectResourceFieldRendererProps) {
  const definition = field.definition;

  if (definition.type !== 'connect_resource') {
    return null;
  }

  return (
    <FormField
      control={form.control}
      name={fieldKey}
      render={({ field: formField }) => (
        <ConnectResourceFieldContent
          formField={formField}
          resourceType={definition.resourceType}
          label={label}
          disabled={disabled}
        />
      )}
    />
  );
}
